import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import {
    avatarImageUrl,
    PlayerDetails,
    PlayerDetailsRaces,
    PlayerItem,
    PlayerService,
    SeasonParticipationInfo,
} from '../service/player.service';
import { DrivingController, DrivingControllerService } from '../service/drivingController.service';
import { DisplayDeviceType } from '../service/displayDevice.service';
import { PlayerSeasonCar, PlayerSeasonCarService } from '../service/playerSeasonCar.service';
import { PlayerHeroComponent } from './components/playerHero.component';
import { PlayerCarsComponent } from './components/playerCars.component';
import { MemberStatsComponent } from '../member/components/memberStats.component';
import { MemberBadgesComponent } from '../member/components/memberBadges.component';
import { MemberRaceHistoryComponent } from '../member/components/memberRaceHistory.component';
import { MemberIncidentsComponent } from '../member/components/memberIncidents.component';

@Component({
    selector: 'app-playerDetails',
    standalone: true,
    templateUrl: './playerDetails.component.html',
    styleUrls: ['./playerDetails.component.scss'],
    imports: [
        CommonModule,
        PlayerHeroComponent,
        PlayerCarsComponent,
        MemberStatsComponent,
        MemberBadgesComponent,
        MemberRaceHistoryComponent,
        MemberIncidentsComponent,
    ],
    providers: [PlayerService, DrivingControllerService, PlayerSeasonCarService],
})
export class PlayerDetailsComponent implements OnInit {
    playerId: string | null = null;

    myPlayerDetails: PlayerDetails = {};
    matchedPlayer?: PlayerItem;
    loading = true;

    // ── Computed stats ────────────────────────────────────────────────────
    numberOfRaces = 0;
    numberOfIncidents = 0;
    bestPlacement?: number;
    bestPlacementCircuit?: string;
    firstRaceDate?: string;

    // ── Driving-controller preference (read-only) ─────────────────────────
    controllers: DrivingController[] = [];
    controllerId: number | null = null;

    // ── Display-device preference (read-only) ─────────────────────────────
    displayDevice: DisplayDeviceType | null = null;

    // ── Season cars maintained by the driver (read-only) ──────────────────
    seasonCars: PlayerSeasonCar[] = [];
    seasonCarsLoading = true;

    constructor(
        private playerService: PlayerService,
        private controllerService: DrivingControllerService,
        private seasonCarService: PlayerSeasonCarService,
        private route: ActivatedRoute,
    ) {}

    ngOnInit() {
        this.route.paramMap.subscribe((params) => {
            this.playerId = params.get('id');
            if (this.playerId == null) return;
            // The component survives in-place navigation between players — reset per param change.
            this.seasonCars = [];
            this.seasonCarsLoading = true;

            this.playerService.getPlayerDetails(this.playerId).subscribe({
                next: (details) => {
                    this.myPlayerDetails = details;
                    this.computeStats();
                    this.loading = false;
                },
                error: () => (this.loading = false),
            });

            // Avatar + preferred controller live on the public players listing.
            this.playerService.getPlayerLarge().subscribe({
                next: (players) => {
                    this.matchedPlayer = players.find(
                        (p) => String(p.player_id) === this.playerId,
                    );
                    this.controllerId = this.matchedPlayer?.controller_id ?? null;
                    this.displayDevice = this.matchedPlayer?.display_device ?? null;
                },
            });

            // Controller catalog (public) to resolve the preference to a label/icon.
            this.controllerService.getAll().subscribe({
                next: (list) => (this.controllers = list),
            });

            // Season cars maintained by the driver (public read).
            this.seasonCarService.getForPlayer(Number(this.playerId)).subscribe({
                next: (entries) => {
                    this.seasonCars = entries;
                    this.seasonCarsLoading = false;
                },
                error: () => (this.seasonCarsLoading = false),
            });
        });
    }

    get races(): PlayerDetailsRaces[] {
        return this.myPlayerDetails.player_races ?? [];
    }

    get participations(): SeasonParticipationInfo[] {
        return this.myPlayerDetails.season_participation ?? [];
    }

    get numericPlayerId(): number | null {
        return this.playerId != null ? Number(this.playerId) : null;
    }

    get avatarUrl(): string | null {
        return this.matchedPlayer ? avatarImageUrl(this.matchedPlayer) : null;
    }

    get name(): string {
        return this.matchedPlayer?.name_gt7 || this.myPlayerDetails.name_gt7 || '';
    }

    get psn(): string | undefined {
        return this.matchedPlayer?.name_psn || this.myPlayerDetails.name_psn;
    }

    get discord(): string | undefined {
        return this.matchedPlayer?.name_discord || this.myPlayerDetails.name_discord;
    }

    private computeStats() {
        const races = this.myPlayerDetails.player_races;
        this.numberOfIncidents = this.myPlayerDetails.player_incidents?.length ?? 0;

        if (!races || races.length === 0) {
            this.numberOfRaces = 0;
            return;
        }

        this.numberOfRaces = races.length;

        // Best placement = lowest finishing position across all races.
        let best: PlayerDetailsRaces | undefined;
        for (const race of races) {
            if (race.position_finished == null) continue;
            if (best == null || race.position_finished < (best.position_finished ?? Infinity)) {
                best = race;
            }
        }
        this.bestPlacement = best?.position_finished;
        this.bestPlacementCircuit = best?.circuits_name;

        const dates = races
            .map((r) => r.day_real)
            .filter((d): d is string => d != null)
            .sort();
        this.firstRaceDate = dates[0];
    }
}
